import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/api";
import { getPracticeId } from "../config/practice";

// Billing tab in Note Review (2026-08-26). Biller-facing day worklist:
// each appointment with a note, its orders mapped to CPT via lcpt_master,
// LCD coverage per line, an LLM medical-necessity check, and an editable
// claim (add/delete/modify CPT + ICD-10) submitted to the internal
// claims_queue (clearinghouse/Athena push is a later phase).
const todayStr = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const CovBadge = ({ coverage }) => {
  if (!coverage || coverage.checked === false)
    return <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">No LCD list</span>;
  return coverage.covered
    ? <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700">LCD ✓ {coverage.matched.slice(0, 2).join(", ")}</span>
    : <span className="text-[11px] px-2 py-0.5 rounded-full bg-red-100 text-red-700">No covered Dx</span>;
};

const BillingTab = ({ showToast }) => {
  const practiseid = getPracticeId();
  const [date, setDate] = useState(todayStr());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [linesByAppt, setLinesByAppt] = useState({});   // appointmentid -> editable lines
  const [analysis, setAnalysis] = useState({});          // appointmentid -> results[]
  const [analyzing, setAnalyzing] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const [queuedIds, setQueuedIds] = useState({});        // appointmentid -> claim_id

  const fetchWorklist = useCallback(async () => {
    setLoading(true);
    setOpenId(null);
    setLinesByAppt({});
    setAnalysis({});
    try {
      const res = await api.post(`/v1/${practiseid}/billing/claims/worklist`, { date });
      const body = res?.data || res;
      const rws = body?.rows || [];
      setRows(rws);
      const q = {};
      rws.forEach((r) => { if (r.queued_claim_id) q[r.appointmentid] = r.queued_claim_id; });
      setQueuedIds(q);
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to load billing worklist", "error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [date, practiseid]);

  useEffect(() => { fetchWorklist(); }, [fetchWorklist]);

  // Build the default editable claim lines for a row: E/M first, then each
  // CPT-mapped order (completed ones included by default), Dx defaults to the
  // LCD-matched documented codes, else the note's codes (max 4 pointers).
  const initLines = (r) => {
    const lines = [];
    if (r.em_line?.cpt) {
      const dx = (r.em_line.coverage?.matched?.length ? r.em_line.coverage.matched : r.note_icd10s).slice(0, 4);
      lines.push({ include: true, cpt: r.em_line.cpt, description: `E/M (LOV ${r.em_line.lov ?? ""})`, icd10s: dx, units: 1, source: "em" });
    }
    (r.orders || []).forEach((o) => {
      const dx = (o.coverage?.matched?.length ? o.coverage.matched
        : o.order_dx?.length ? o.order_dx : r.note_icd10s).slice(0, 4);
      lines.push({
        include: o.status === "COMPLETED" && !!o.cpt,
        cpt: o.cpt || "", description: `${o.ordername} (${o.status})`,
        icd10s: dx, units: 1, source: "order", coverage: o.coverage, status: o.status,
      });
    });
    return lines;
  };

  const openRow = (r) => {
    setOpenId(openId === r.appointmentid ? null : r.appointmentid);
    if (!linesByAppt[r.appointmentid]) {
      setLinesByAppt((m) => ({ ...m, [r.appointmentid]: initLines(r) }));
    }
  };

  const setLine = (apptId, idx, patch) => {
    setLinesByAppt((m) => {
      const lines = [...(m[apptId] || [])];
      lines[idx] = { ...lines[idx], ...patch };
      return { ...m, [apptId]: lines };
    });
  };

  const addLine = (apptId) => {
    setLinesByAppt((m) => ({
      ...m,
      [apptId]: [...(m[apptId] || []), { include: true, cpt: "", description: "Manual line", icd10s: [], units: 1, source: "manual" }],
    }));
  };

  const removeLine = (apptId, idx) => {
    setLinesByAppt((m) => {
      const lines = [...(m[apptId] || [])];
      lines.splice(idx, 1);
      return { ...m, [apptId]: lines };
    });
  };

  const runAnalyze = async (r) => {
    setAnalyzing(r.appointmentid);
    try {
      const res = await api.post(`/v1/${practiseid}/billing/claims/analyze`, {
        date, patientid: r.patientid, appointmentid: r.appointmentid,
      });
      const body = res?.data || res;
      if (body?.status === 200) setAnalysis((m) => ({ ...m, [r.appointmentid]: body.results || [] }));
      else showToast(body?.message || "Necessity check failed", "error");
    } catch (e) {
      showToast(e?.response?.data?.message || "Necessity check failed", "error");
    } finally {
      setAnalyzing(null);
    }
  };

  const submit = async (r) => {
    const lines = (linesByAppt[r.appointmentid] || [])
      .filter((l) => l.include && l.cpt.trim())
      .map((l) => ({ cpt: l.cpt.trim(), description: l.description, icd10s: l.icd10s, units: l.units || 1, source: l.source }));
    if (!lines.length) { showToast("No included lines with a CPT", "info"); return; }
    if (lines.some((l) => !l.icd10s.length)) { showToast("Every line needs at least one ICD-10", "info"); return; }
    setSubmitting(r.appointmentid);
    try {
      const res = await api.post(`/v1/${practiseid}/billing/claims/submit`, {
        date, patientid: r.patientid, appointmentid: r.appointmentid,
        note_recording_id: r.note_recording_id, lines,
      });
      const body = res?.data || res;
      if (body?.status === 200) {
        setQueuedIds((m) => ({ ...m, [r.appointmentid]: body.claim_id }));
        showToast(`Claim queued (#${body.claim_id})`, "success");
      } else showToast(body?.message || "Submit failed", "error");
    } catch (e) {
      showToast(e?.response?.data?.message || "Submit failed", "error");
    } finally {
      setSubmitting(null);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.post(`/v1/${practiseid}/billing/claims/queue`, { date });
      const body = res?.data || res;
      const claims = body?.claims || [];
      if (!claims.length) { showToast("No queued claims for this date", "info"); return; }
      const csvRows = [["claim_id", "dos", "patientid", "patient_name", "appointmentid", "cpt", "icd10s", "units", "description", "created_by"]];
      claims.forEach((c) => (c.lines || []).forEach((l) => {
        csvRows.push([c.claim_id, c.dos, c.patientid, c.patient_name, c.appointmentid,
          l.cpt, (l.icd10s || []).join("|"), l.units, (l.description || "").replace(/[",\n]/g, " "), c.created_by]);
      }));
      const csv = csvRows.map((r) => r.map((v) => `"${String(v ?? "")}"`).join(",")).join("\n");
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = `claims_${date}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast("Export failed", "error");
    }
  };

  const summary = useMemo(() => {
    const total = rows.length;
    const queued = rows.filter((r) => queuedIds[r.appointmentid]).length;
    const flagged = rows.filter((r) => (r.orders || []).some((o) => o.status === "COMPLETED" && o.coverage?.checked && !o.coverage?.covered)).length;
    return { total, queued, flagged };
  }, [rows, queuedIds]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input type="date" value={date} max={todayStr()} onChange={(e) => setDate(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        <span className="text-sm text-gray-500 mr-auto">
          {loading ? "Loading…" : `${summary.total} visits · ${summary.queued} queued · ${summary.flagged} with LCD flags`}
        </span>
        <button onClick={fetchWorklist} className="px-3 py-2 rounded-lg text-sm font-semibold bg-[#1D2951] text-white">Refresh</button>
        <button onClick={exportCsv} className="px-3 py-2 rounded-lg text-sm font-semibold border border-[#1D2951] text-[#1D2951]">Export queued CSV</button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {!loading && rows.length === 0 && <div className="text-gray-400 text-sm p-8 text-center">No visits with notes on this date.</div>}
        {rows.map((r) => {
          const open = openId === r.appointmentid;
          const lines = linesByAppt[r.appointmentid] || [];
          const anl = analysis[r.appointmentid];
          const anlByOrder = {};
          (anl || []).forEach((x) => { anlByOrder[x.orderid] = x; });
          return (
            <div key={r.appointmentid} className="border-b last:border-b-0">
              <button onClick={() => openRow(r)} className={`w-full text-left px-4 py-3 flex flex-wrap items-center gap-3 ${open ? "bg-[#EEF2FB]" : "hover:bg-gray-50"}`}>
                <span className="font-semibold text-sm text-gray-800 w-48 truncate">{r.patient_name || `Patient ${r.patientid}`}</span>
                <span className="text-xs text-gray-500 w-40 truncate">{r.providername || "—"}</span>
                <span className="text-xs text-gray-500 w-28 truncate">{r.purpose || ""}</span>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${r.note_status === "APPROVED" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                  {r.note_status === "APPROVED" ? "Note signed" : "Note unsigned"}
                </span>
                {r.em_line?.cpt && <span className="text-[11px] font-mono bg-gray-100 px-2 py-0.5 rounded">E/M {r.em_line.cpt}</span>}
                <span className="text-xs text-gray-500">{(r.orders || []).length} orders</span>
                <span className="ml-auto">
                  {queuedIds[r.appointmentid]
                    ? <span className="text-xs font-semibold text-green-700">Queued ✓ #{queuedIds[r.appointmentid]}</span>
                    : <span className="text-xs text-gray-400">{open ? "▲" : "▼"}</span>}
                </span>
              </button>

              {open && (
                <div className="px-4 pb-4 bg-[#FAFBFF]">
                  <div className="flex items-center gap-2 py-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mr-auto">Claim lines</span>
                    <button onClick={() => runAnalyze(r)} disabled={analyzing === r.appointmentid}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-[#B45309] text-[#B45309] disabled:opacity-50">
                      {analyzing === r.appointmentid ? "Checking necessity…" : "Run necessity check"}
                    </button>
                    <button onClick={() => addLine(r.appointmentid)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-gray-400 text-gray-600">+ Add line</button>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[11px] text-gray-500 uppercase">
                          <th className="text-left py-1 pr-2">Bill</th>
                          <th className="text-left py-1 pr-2">CPT</th>
                          <th className="text-left py-1 pr-2">Description</th>
                          <th className="text-left py-1 pr-2">ICD-10 (comma separated)</th>
                          <th className="text-left py-1 pr-2">LCD</th>
                          <th className="text-left py-1 pr-2">Necessity</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {lines.map((l, idx) => {
                          const a = l.source === "order"
                            ? Object.values(anlByOrder).find((x) => l.description.startsWith(x.ordername)) : null;
                          return (
                            <tr key={idx} className="border-t border-gray-100">
                              <td className="py-1.5 pr-2"><input type="checkbox" checked={l.include} onChange={(e) => setLine(r.appointmentid, idx, { include: e.target.checked })} /></td>
                              <td className="py-1.5 pr-2">
                                <input value={l.cpt} onChange={(e) => setLine(r.appointmentid, idx, { cpt: e.target.value })}
                                  placeholder="CPT" className={`border rounded px-2 py-1 w-20 font-mono text-xs ${!l.cpt ? "border-amber-400 bg-amber-50" : "border-gray-300"}`} />
                              </td>
                              <td className="py-1.5 pr-2 text-xs text-gray-700">{l.description}</td>
                              <td className="py-1.5 pr-2">
                                <input value={l.icd10s.join(", ")}
                                  onChange={(e) => setLine(r.appointmentid, idx, { icd10s: e.target.value.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean) })}
                                  placeholder="ICD-10s" className="border border-gray-300 rounded px-2 py-1 w-56 font-mono text-xs" />
                              </td>
                              <td className="py-1.5 pr-2">{l.source !== "manual" ? <CovBadge coverage={l.source === "em" ? r.em_line?.coverage : l.coverage} /> : null}</td>
                              <td className="py-1.5 pr-2">
                                {a ? (a.documented
                                  ? <span className="text-[11px] text-green-700" title={a.citation}>✓ documented</span>
                                  : <span className="text-[11px] text-amber-800" title={a.gap}>⚠ {a.gap ? "gap" : "not documented"}</span>) : null}
                              </td>
                              <td className="py-1.5 text-right">
                                <button onClick={() => removeLine(r.appointmentid, idx)} className="text-gray-400 hover:text-red-600 text-sm">×</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {anl && anl.some((x) => x.gap) && (
                    <div className="mt-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
                      {anl.filter((x) => x.gap).map((x) => <div key={x.orderid}>⚠ <b>{x.ordername}</b>: {x.gap}</div>)}
                      <div className="text-gray-500 mt-1">Suggested note edits can be accepted by the provider in the Notes tab (Billing Sanity Check).</div>
                    </div>
                  )}

                  <div className="flex items-center gap-3 mt-3">
                    <span className="text-xs text-gray-400 mr-auto">Documented Dx: {r.note_icd10s.join(", ") || "none"}</span>
                    <button onClick={() => submit(r)} disabled={submitting === r.appointmentid}
                      className="px-4 py-2 rounded-lg text-sm font-semibold bg-[#1D2951] text-white disabled:bg-gray-400">
                      {submitting === r.appointmentid ? "Submitting…" : queuedIds[r.appointmentid] ? "Re-submit (replaces queued)" : "Submit claim"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BillingTab;
